// web/geneCard.ts — the COLLECTIBLE GENE CARD: one shared look for a gene everywhere.
//
// A gene is loot, so it should READ like loot — a rarity band, its name, the caste it
// suits, its Power, a few human "+N% <Stat>" lines, the generation it belongs to, and
// its actions. Both the inventory (geneInventory.ts) and the box reveal (ui.ts) build
// their cards from THIS module, so a gene looks identical wherever it appears.
//
// PURE + RENDER-ONLY: every function here is a pure string/number transform over a tiny
// descriptor (type, rarity, trait-count). It never touches the sim, the save, or any
// hash, draws no RNG, and — the hard rule — NEVER prints a raw modifier string, internal
// id, serial, or channel/procedural integer as visible text. Ids live in data-* only.
// The "+N% Stat" magnitudes are a DISPLAY model (a consistent, readable scaling of a
// gene's EFFECT_CHANNEL makeup), not the sim's exact channel vector — which the view
// models (GeneRowView / RewardGene) don't carry — so we derive a stable, sensible
// percentage read from what they DO carry: body-part type, rarity, and trait count.

import { EFFECT_CHANNEL } from '../src/types.ts';
import type { EffectChannel } from '../src/types.ts';
import {
  GENE_TYPE_BASE_CHANNEL, RARITY_NAME,
} from '../src/sim/geneData.ts';

// --- channel → human stat name ----------------------------------------------
//
// The brief's mapping: Combat→Attack, Defense→Armor, Movement→Speed, Toxicity→Venom,
// Sensing→Vision. Regen/Size are support channels with no headline stat — they fold
// into the perk flavour line, never a "+N%" stat, so the card only ever shows the five
// combat-relevant stats a player compares loot on.
const CHANNEL_STAT: Readonly<Partial<Record<EffectChannel, string>>> = {
  [EFFECT_CHANNEL.Combat]: 'Attack',
  [EFFECT_CHANNEL.Defense]: 'Armor',
  [EFFECT_CHANNEL.Movement]: 'Speed',
  [EFFECT_CHANNEL.Toxicity]: 'Venom',
  [EFFECT_CHANNEL.Sensing]: 'Vision',
};

/**
 * A consistent SECONDARY channel per body-part type — the stat a gene's mutations tend
 * to push alongside its primary, so each type reads with a stable two-stat identity
 * (e.g. a Mandible = Attack + a little Armor; a Wing = Speed + Vision). Indexed by
 * GENE_TYPE (Wing, Mandible, VenomGland, Carapace, Antenna, Limb). Display-only flavour;
 * chosen to never duplicate the type's primary channel.
 */
const TYPE_SECONDARY_CHANNEL: readonly EffectChannel[] = [
  EFFECT_CHANNEL.Sensing,   // Wing (primary Speed)      → + Vision
  EFFECT_CHANNEL.Defense,   // Mandible (primary Attack) → + Armor
  EFFECT_CHANNEL.Combat,    // VenomGland (primary Venom)→ + Attack
  EFFECT_CHANNEL.Combat,    // Carapace (primary Armor)  → + Attack
  EFFECT_CHANNEL.Movement,  // Antenna (primary Vision)  → + Speed
  EFFECT_CHANNEL.Defense,   // Limb (primary Speed)      → + Armor
];

/** One human stat line, e.g. { stat: 'Attack', pct: 18 }. */
export interface StatLine { stat: string; pct: number; }

/**
 * The display magnitude (whole %) for a gene's PRIMARY stat: a clean band that widens
 * with rarity so a higher-tier gene visibly reads stronger — Common 10% … Legendary 22%
 * (10 + rarity·3). Stays inside the "dominant ~10–25%" range the brief asks for.
 */
function primaryPct(rarity: number): number {
  const r = rarity < 0 ? 0 : rarity > 4 ? 4 : rarity;
  return 10 + r * 3; // 10, 13, 16, 19, 22
}

/**
 * Turn a gene into 1–3 human "+N% <Stat>" lines, strongest-first, from its EFFECT_CHANNEL
 * makeup. Pure + deterministic (same descriptor ⇒ same lines), no raw integers exposed.
 *
 *  • PRIMARY  — the body-part type's base channel (GENE_TYPE_BASE_CHANNEL), at primaryPct.
 *  • SECONDARY— present once the gene has any mutations (traitTotal > 0): the type's stable
 *               secondary channel, scaled by trait count but always smaller than primary.
 *  • TERTIARY — only for richly-mutated Epic+ genes: a small Speed/Vision splash, so a
 *               top-tier gene reads with a third stat without ever overstating it.
 *
 * Magnitudes are a readable display model, NOT the sim's exact channel vector (which the
 * view models don't carry) — consistent and ordered, never fake per-point precision.
 */
export function geneStatLines(type: number, rarity: number, traitTotal: number): StatLine[] {
  const out: StatLine[] = [];
  const seen = new Set<string>();
  const push = (channel: EffectChannel | undefined, pct: number): void => {
    if (channel === undefined || pct <= 0) return;
    const stat = CHANNEL_STAT[channel];
    if (!stat || seen.has(stat)) return;
    seen.add(stat);
    out.push({ stat, pct });
  };

  const baseCh = GENE_TYPE_BASE_CHANNEL[type];
  const prim = primaryPct(rarity);
  push(baseCh, prim);

  if (traitTotal > 0) {
    // Secondary grows with mutation stacks but is capped a few points under primary so
    // the dominant stat always leads. Floor at 4% so a listed stat never reads as noise.
    const sec = Math.max(4, Math.min(prim - 4, 4 + traitTotal * 3));
    push(TYPE_SECONDARY_CHANNEL[type], sec);
  }

  // A third stat only for the genuinely loaded drops (Epic+ with several mutations).
  if (rarity >= 3 && traitTotal >= 3) {
    const tert = Math.max(4, Math.min(8, 3 + traitTotal));
    // Splash toward whichever of Speed/Vision the card isn't already showing.
    push(EFFECT_CHANNEL.Movement, tert);
    push(EFFECT_CHANNEL.Sensing, tert);
  }

  return out.sort((a, b) => b.pct - a.pct).slice(0, 3);
}

// --- HTML pieces -------------------------------------------------------------

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) => (c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : '&quot;'));
}

/** The colored RARITY band shown as its human NAME (never the integer). */
export function rarityBandHTML(rarity: number): string {
  const name = RARITY_NAME[rarity] ?? '?';
  return `<span class="gc-band rcol-${rarity}" title="${esc(name)} rarity">${esc(name)}</span>`;
}

/** The stat lines as compact "+N% Stat" chips, strongest-first (empty string for none). */
export function statLinesHTML(type: number, rarity: number, traitTotal: number): string {
  const lines = geneStatLines(type, rarity, traitTotal);
  if (lines.length === 0) return '';
  const chips = lines
    .map((l, i) => `<span class="gc-stat${i === 0 ? ' lead' : ''}">+${l.pct}% ${esc(l.stat)}</span>`)
    .join('');
  return `<div class="gc-stats" title="Estimated stat profile if equipped">${chips}</div>`;
}

/**
 * The "Generation N" line. There is no clean per-gene looted-at generation on the gene
 * view models (the gene SoA's source tick is not surfaced, and surfacing it would be a
 * sim change), so we show the COLONY's current generation as context — the generation a
 * gene applied now would enter the brood as. Pass `generation` from
 * `session.playerGeneration()`; omit (undefined) to render nothing rather than invent a
 * fake per-gene number.
 */
export function generationHTML(generation: number | undefined): string {
  if (generation === undefined || !Number.isFinite(generation)) return '';
  return `<span class="gc-gen" title="Your colony's current generation — the brood this gene would join">Generation ${Math.max(0, Math.floor(generation))}</span>`;
}

/**
 * Format a power value so it stays HUMAN-READABLE and never shows a huge meaningless
 * string: thousands separators under 100k, then `k`/`M` abbreviation above (defensive —
 * realistic values sit in the 10s–1000s, but a pathological late-game stack can't render
 * as a wall of digits). The single power formatter shared across the HUD, cards, and lists.
 */
export function fmtPower(n: number): string {
  const v = Math.round(n);
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 100_000) return `${Math.round(v / 1000)}k`;
  return v.toLocaleString('en-US');
}

/** Power as a human chip (the Power Score; the ⚡ is decoration, the number is the value). */
export function powerHTML(power: number): string {
  return `<span class="gc-power" title="Power Score">Power ${fmtPower(power)}</span>`;
}
